import { type ReactNode, useMemo } from 'react';
import { isMac } from '@gonfalon/browser-platform/isMac';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';

import type { CommandPaletteError } from '../command-palette-error';
import { useCommandExecution } from '../hooks/use-command-execution';
import { useCommandPaletteActions } from '../hooks/use-command-palette-actions';
import { useCommandPaletteKeyboardManagement } from '../hooks/use-command-palette-keyboard-management';
import { useCommandPaletteState } from '../hooks/use-command-palette-state';
import { priorityFilterStrategy } from '../strategies/priority-filter-strategy';
import type { Command } from '../types/command';
import type { CommandPaletteEvent } from '../types/command-palette-events';
import type { CommandRankingStrategy } from '../types/command-strategy';

import { CommandPaletteAutocomplete } from './command-palette-autocomplete';
import { CommandPaletteDialog } from './command-palette-dialog';
import {
  CommandPaletteEmptyStatesContext,
  type CommandPaletteEmptyStatesContextValue,
} from './command-palette-empty-states-context';
import { CommandPaletteModal } from './command-palette-modal';
import {
  CommandPaletteStateProvider,
  useInternalCommandPaletteActions,
  useInternalCommandPaletteState,
} from './command-palette-state-provider';
import { CommandPaletteStrategyContext } from './command-palette-strategy-context';
import {
  CommandPaletteTelemetryContext,
  type CommandPaletteTelemetryContextValue,
} from './command-palette-telemetry-context';
import { CommandPaletteViewShell } from './command-palette-view-shell';
import { CommandRegistryProvider, useCommandRegistry } from './command-registry-provider';
import { DisabledCommandPaletteStateProvider } from './disabled-command-palette-state-provider';
import { DisabledCommandRegistryProvider } from './disabled-command-registry-provider';
import { SnapPrefixContext } from './snap-prefix-context';

/**
 * Thin context provider that exposes `onEvent` and `onError` as a
 * single stable value. Identity of the context value is memoised on
 * the callback identities, so consumers that subscribe never re-
 * render until the consumer actually passes new callbacks.
 */
function CommandPaletteTelemetryProvider({
  children,
  onEvent,
  onError,
}: {
  children: ReactNode;
  onEvent: CommandPaletteTelemetryContextValue['onEvent'];
  onError: CommandPaletteTelemetryContextValue['onError'];
}) {
  const value = useMemo<CommandPaletteTelemetryContextValue>(() => ({ onEvent, onError }), [onEvent, onError]);
  return <CommandPaletteTelemetryContext.Provider value={value}>{children}</CommandPaletteTelemetryContext.Provider>;
}

function defaultGlobalShortcut() {
  return isMac() ? 'meta+k' : 'ctrl+k';
}

export interface CommandPaletteProviderProps {
  children: ReactNode;
  onEvent: (event: CommandPaletteEvent) => void;
  onError: (error: CommandPaletteError) => void;
  globalShortcut?: string;
  /**
   * Snap prefix string. Defaults to `'@'`. Common choices: `'@'`,
   * `'>'`, `'in:'`. The prefix is treated literally; whatever follows
   * it (up to the next whitespace) is interpreted as the snap key.
   */
  snapPrefix?: string;
  /**
   * Ranking strategy controlling filter + sort. Defaults to
   * `priorityFilterStrategy` (predictable, muscle-memory-preserving).
   * Pass `relevanceStrategy` for Spotlight/Raycast-style score blending,
   * or a custom strategy implementing `CommandRankingStrategy`.
   */
  rankingStrategy?: CommandRankingStrategy;
  /**
   * Main-mode "no commands matched" copy. Rendered when the user is
   * in main mode (no scope entered) and the query produced no
   * matches. The command-level `emptyState` field is consulted only
   * inside an entered root; this hook is the only way to contribute
   * consumer copy to the main-mode listbox.
   *
   * Returning `undefined` falls back to the palette's generic
   * "No matches" message -- consumers opt in granularly (e.g. only
   * render bespoke copy for non-empty queries).
   */
  mainEmptyState?: CommandPaletteEmptyStatesContextValue['mainEmptyState'];
  /**
   * Main-mode loading-state copy. Rendered in main mode when any
   * command in the registry is `status: 'pending'` -- the palette
   * defers the "no matches" claim until at least one declared scope
   * has settled. Returning `undefined` falls back to the palette's
   * generic "Searching\u2026" message.
   */
  mainLoadingState?: CommandPaletteEmptyStatesContextValue['mainLoadingState'];
}

/**
 * Inner content of the palette. Subscribes to internal state (full
 * shape) and renders the open palette UI. Mounted as a sibling of
 * the app tree so re-renders are scoped to the overlay subtree
 * rather than fanning out across the whole app.
 */
function CommandPaletteContent({
  globalShortcut,
  isOpen,
  onOpenChange,
}: {
  globalShortcut: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}) {
  const state = useInternalCommandPaletteState();
  const actions = useInternalCommandPaletteActions();
  const { openPalette, closePalette } = useCommandPaletteActions();
  const { commands } = useCommandRegistry();
  const { executeCommandById } = useCommandExecution();

  // Live derivations of state for the rest of the tree.
  const query = state.mode.type === 'open' ? state.mode.query : '';
  const rootCommandId = state.mode.type === 'open' ? state.mode.rootCommandId : null;
  const viewCommand = state.mode.type === 'view' ? state.mode.command : undefined;
  const viewPrefill = state.mode.type === 'view' ? state.mode.prefill : undefined;
  const viewEntryId = state.mode.type === 'view' ? state.mode.entryId : undefined;
  const rootCommand: Command | undefined = rootCommandId !== null ? commands[rootCommandId] : undefined;

  useCommandPaletteKeyboardManagement({
    shortcut: globalShortcut,
    executeCommandById,
    openPalette,
    closePalette,
  });

  // Snap commit: parser hands us a Command; dispatch openRoot with
  // its id and the rest text as the in-mode query. Internal action;
  // public surface doesn't expose openRoot.
  const onSnapCommit = (command: Command, initialQuery: string) => {
    actions.openRoot(command.id, initialQuery);
  };

  return (
    <CommandPaletteModal isOpen={isOpen} onOpenChange={onOpenChange}>
      <CommandPaletteDialog>
        {viewCommand !== undefined ? (
          <CommandPaletteViewShell
            command={viewCommand}
            prefill={viewPrefill}
            entryId={viewEntryId}
            onBack={actions.exitView}
            onClose={closePalette}
          />
        ) : (
          <CommandPaletteAutocomplete
            query={query}
            onQueryChange={actions.setQuery}
            rootCommandId={rootCommandId}
            rootCommand={rootCommand}
            onAction={async (commandId) => {
              await executeCommandById(commandId, {
                executionType: 'action-execution',
                message: `Failed to execute action command: ${String(commandId)}`,
              });
            }}
            onClose={closePalette}
            onExitRoot={actions.exitRoot}
            onSnapCommit={onSnapCommit}
          />
        )}
      </CommandPaletteDialog>
    </CommandPaletteModal>
  );
}

function CommandPaletteOverlay({ globalShortcut }: { globalShortcut: string }) {
  const { isOpen } = useCommandPaletteState();
  const { openPalette, closePalette } = useCommandPaletteActions();

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (nextIsOpen) {
      openPalette('button');
    } else {
      closePalette();
    }
  };

  // No Suspense boundary inside the palette tree. The palette never
  // suspends -- filtering is synchronous (Fuse over the resolved
  // store), consumer fetches use non-suspense queries with
  // `placeholderData: keepPreviousData`. The input never unmounts on
  // keystroke; the bug that made characters disappear ("react" -> "ract")
  // is killed structurally rather than positionally.
  return (
    <ErrorBoundary severity="medium" fallback={null}>
      <CommandPaletteContent globalShortcut={globalShortcut} isOpen={isOpen} onOpenChange={handleOpenChange} />
    </ErrorBoundary>
  );
}

function CommandPaletteProviderInternal({
  children,
  globalShortcut = defaultGlobalShortcut(),
}: Omit<CommandPaletteProviderProps, 'onEvent' | 'onError' | 'snapPrefix' | 'rankingStrategy'>) {
  // IMPORTANT: do NOT subscribe to CommandPaletteStateContext here.
  // Doing so would re-render `{children}` -- i.e. the entire app --
  // on every keystroke into the palette. The subscription lives in
  // CommandPaletteOverlay as a sibling instead, so re-renders are
  // scoped to the overlay subtree.
  return (
    <>
      {children}
      <CommandPaletteOverlay globalShortcut={globalShortcut} />
    </>
  );
}

/**
 * Global command palette provider. Wraps the app with the registry,
 * state, and strategy contexts; renders the palette overlay as a
 * sibling of `{children}`.
 *
 * @example
 * ```tsx
 * <CommandPaletteProvider
 *   onEvent={(event) => trackEvent(event)}
 *   onError={(error) => trackError(error, { severity: 'medium' })}
 *   globalShortcut="meta+k"
 *   snapPrefix="@"
 *   rankingStrategy={priorityFilterStrategy}
 * >
 *   <App />
 * </CommandPaletteProvider>
 * ```
 */
export function CommandPaletteProvider({
  children,
  onEvent,
  onError,
  globalShortcut,
  snapPrefix = '@',
  rankingStrategy = priorityFilterStrategy,
  mainEmptyState,
  mainLoadingState,
}: CommandPaletteProviderProps) {
  // Stable identity across renders as long as the consumer's resolver
  // callbacks are stable. Memo on the callback identities -- consumers
  // that pass inline arrow functions will recompute every render, same
  // contract as the telemetry context's onEvent/onError.
  const emptyStatesValue = useMemo<CommandPaletteEmptyStatesContextValue>(
    () => ({ mainEmptyState, mainLoadingState }),
    [mainEmptyState, mainLoadingState],
  );
  return (
    <ErrorBoundary
      severity="medium"
      fallbackRender={() => (
        <DisabledCommandRegistryProvider>
          <DisabledCommandPaletteStateProvider>{children}</DisabledCommandPaletteStateProvider>
        </DisabledCommandRegistryProvider>
      )}
    >
      <CommandPaletteTelemetryProvider onEvent={onEvent} onError={onError}>
        <SnapPrefixContext.Provider value={snapPrefix}>
          <CommandPaletteStrategyContext.Provider value={rankingStrategy}>
            <CommandPaletteEmptyStatesContext.Provider value={emptyStatesValue}>
              <CommandRegistryProvider>
                <CommandPaletteStateProvider>
                  <CommandPaletteProviderInternal globalShortcut={globalShortcut}>
                    {children}
                  </CommandPaletteProviderInternal>
                </CommandPaletteStateProvider>
              </CommandRegistryProvider>
            </CommandPaletteEmptyStatesContext.Provider>
          </CommandPaletteStrategyContext.Provider>
        </SnapPrefixContext.Provider>
      </CommandPaletteTelemetryProvider>
    </ErrorBoundary>
  );
}
